import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  makeStyles,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  Add,
  DeleteOutline,
  Edit,
  PlayCircleOutline,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import MarkdownWrapper from "../../components/MarkdownWrapper";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    margin: 20,
    minHeight: 0,
    overflow: "hidden",
    display: "grid",
    gridTemplateColumns: "320px minmax(0, 1fr)",
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  sidebar: {
    borderRight: "1px solid rgba(0, 0, 0, 0.08)",
    background: "#fafafa",
    overflowY: "auto",
    padding: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      borderRight: 0,
      borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
      maxHeight: 300,
    },
  },
  sidebarHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  module: {
    marginBottom: theme.spacing(1.25),
  },
  moduleHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(0.75),
    minHeight: 36,
  },
  moduleTitle: {
    fontWeight: 700,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  moduleActions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  topicButton: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.9, 1),
    border: 0,
    borderRadius: 6,
    background: "transparent",
    cursor: "pointer",
    textAlign: "left",
    color: theme.palette.text.primary,
    "&:hover": {
      background: "rgba(0, 0, 0, 0.04)",
    },
  },
  topicButtonActive: {
    background: "rgba(63, 81, 181, 0.1)",
    color: theme.palette.primary.main,
  },
  topicNumber: {
    width: 34,
    flexShrink: 0,
    color: "inherit",
    fontWeight: 700,
  },
  topicText: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  workspace: {
    minWidth: 0,
    minHeight: 0,
    overflowY: "auto",
    background: "#fff",
    padding: theme.spacing(2),
    ...theme.scrollbarStyles,
  },
  courseHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  topicActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
  inactiveBadge: {
    display: "inline-flex",
    alignItems: "center",
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0.25, 0.75),
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    color: "#8a4b00",
    background: "#fff2d6",
  },
  videoShell: {
    width: "100%",
    aspectRatio: "16 / 9",
    background: "#111827",
    borderRadius: 6,
    overflow: "hidden",
    marginBottom: theme.spacing(2),
  },
  iframe: {
    width: "100%",
    height: "100%",
    border: 0,
  },
  videoPlaceholder: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    color: "#fff",
    textAlign: "center",
    padding: theme.spacing(2),
  },
  contentBlock: {
    borderTop: "1px solid rgba(0, 0, 0, 0.08)",
    paddingTop: theme.spacing(2),
    lineHeight: 1.65,
    "& p": {
      marginTop: 0,
    },
    "& a": {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },
  emptyState: {
    minHeight: 420,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: theme.palette.text.secondary,
    gap: theme.spacing(1),
  },
  formGrid: {
    display: "grid",
    gap: theme.spacing(2),
    minWidth: 520,
    [theme.breakpoints.down("xs")]: {
      minWidth: 0,
    },
  },
  preview: {
    padding: theme.spacing(1.5),
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 6,
    background: "#fafafa",
  },
}));

const moduleInitialValues = {
  title: "",
  position: 0,
  isActive: true,
};

const topicInitialValues = {
  moduleId: "",
  title: "",
  youtubeUrl: "",
  content: "",
  position: 0,
  isActive: true,
};

const getYoutubeId = url => {
  if (!url) return "";
  const value = String(url).trim();
  const patterns = [
    /youtu\.be\/([^?&#/]+)/,
    /youtube\.com\/watch\?v=([^?&#/]+)/,
    /youtube\.com\/embed\/([^?&#/]+)/,
    /youtube\.com\/shorts\/([^?&#/]+)/,
  ];

  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (match?.[1]) return match[1];
  }

  return "";
};

const sortByPosition = items =>
  [...(items || [])].sort((a, b) => {
    const positionDiff = Number(a.position || 0) - Number(b.position || 0);
    if (positionDiff !== 0) return positionDiff;
    return Number(a.id || 0) - Number(b.id || 0);
  });

const Tutorials = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [modules, setModules] = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [moduleModalOpen, setModuleModalOpen] = useState(false);
  const [topicModalOpen, setTopicModalOpen] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [editingTopic, setEditingTopic] = useState(null);
  const [moduleValues, setModuleValues] = useState(moduleInitialValues);
  const [topicValues, setTopicValues] = useState(topicInitialValues);

  const canManage = ["superadmin", "owner"].includes(user?.profile);

  const orderedModules = useMemo(
    () =>
      sortByPosition(modules).map(module => ({
        ...module,
        topics: sortByPosition(module.topics),
      })),
    [modules]
  );

  const topics = useMemo(
    () => orderedModules.flatMap(module => module.topics || []),
    [orderedModules]
  );

  const selectedTopic = useMemo(
    () => topics.find(topic => Number(topic.id) === Number(selectedTopicId)) || topics[0],
    [topics, selectedTopicId]
  );

  const selectedModule = useMemo(
    () =>
      orderedModules.find(module =>
        module.topics?.some(topic => Number(topic.id) === Number(selectedTopic?.id))
      ),
    [orderedModules, selectedTopic]
  );

  const loadTutorials = async (preferredTopicId = null) => {
    setLoading(true);
    try {
      const { data } = await api.get("/tutorials");
      setModules(data.modules || []);
      const nextTopics = (data.modules || []).flatMap(module => module.topics || []);
      if (preferredTopicId) {
        setSelectedTopicId(preferredTopicId);
      } else if (!selectedTopicId && nextTopics[0]) {
        setSelectedTopicId(nextTopics[0].id);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTutorials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openModuleModal = module => {
    setEditingModule(module || null);
    setModuleValues(
      module
        ? {
            title: module.title || "",
            position: Number(module.position || 0),
            isActive: module.isActive !== false,
          }
        : moduleInitialValues
    );
    setModuleModalOpen(true);
  };

  const openTopicModal = (topic, moduleId) => {
    setEditingTopic(topic || null);
    setTopicValues(
      topic
        ? {
            moduleId: topic.moduleId || moduleId || "",
            title: topic.title || "",
            youtubeUrl: topic.youtubeUrl || "",
            content: topic.content || "",
            position: Number(topic.position || 0),
            isActive: topic.isActive !== false,
          }
        : {
            ...topicInitialValues,
            moduleId: moduleId || orderedModules[0]?.id || "",
          }
    );
    setTopicModalOpen(true);
  };

  const saveModule = async () => {
    try {
      const payload = {
        ...moduleValues,
        position: Number(moduleValues.position || 0),
      };

      if (editingModule) {
        await api.put(`/tutorial-modules/${editingModule.id}`, payload);
      } else {
        await api.post("/tutorial-modules", payload);
      }

      setModuleModalOpen(false);
      toast.success("Módulo guardado");
      await loadTutorials();
    } catch (err) {
      toastError(err);
    }
  };

  const saveTopic = async () => {
    try {
      const payload = {
        ...topicValues,
        moduleId: Number(topicValues.moduleId),
        position: Number(topicValues.position || 0),
      };

      if (editingTopic) {
        await api.put(`/tutorial-topics/${editingTopic.id}`, payload);
      } else {
        const { data } = await api.post(
          `/tutorial-modules/${payload.moduleId}/topics`,
          payload
        );
        setSelectedTopicId(data.id);
        await loadTutorials(data.id);
        setTopicModalOpen(false);
        toast.success("Tópico guardado");
        return;
      }

      setTopicModalOpen(false);
      toast.success("Tópico guardado");
      await loadTutorials();
    } catch (err) {
      toastError(err);
    }
  };

  const removeModule = async module => {
    if (!window.confirm(`Eliminar el módulo "${module.title}" y sus tópicos?`)) return;
    try {
      await api.delete(`/tutorial-modules/${module.id}`);
      toast.success("Módulo eliminado");
      setSelectedTopicId(null);
      await loadTutorials();
    } catch (err) {
      toastError(err);
    }
  };

  const removeTopic = async topic => {
    if (!window.confirm(`Eliminar el tópico "${topic.title}"?`)) return;
    try {
      await api.delete(`/tutorial-topics/${topic.id}`);
      toast.success("Tópico eliminado");
      setSelectedTopicId(null);
      await loadTutorials();
    } catch (err) {
      toastError(err);
    }
  };

  const videoId = getYoutubeId(selectedTopic?.youtubeUrl);

  return (
    <MainContainer>
      <MainHeader>
        <Title>Tutoriales</Title>
        {canManage && (
          <MainHeaderButtonsWrapper>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => openModuleModal()}
            >
              Módulo
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              disabled={!orderedModules.length}
              onClick={() => openTopicModal(null, selectedModule?.id || orderedModules[0]?.id)}
            >
              Tópico
            </Button>
          </MainHeaderButtonsWrapper>
        )}
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <aside className={classes.sidebar}>
          <div className={classes.sidebarHeader}>
            <Typography variant="subtitle2">Contenido</Typography>
            {loading && <CircularProgress size={18} />}
          </div>

          {!orderedModules.length && !loading && (
            <div className={classes.emptyState}>
              <Typography variant="subtitle1">No hay tutoriales disponibles</Typography>
              {canManage && (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<Add />}
                  onClick={() => openModuleModal()}
                >
                  Crear módulo
                </Button>
              )}
            </div>
          )}

          {orderedModules.map((module, moduleIndex) => (
            <div key={module.id} className={classes.module}>
              <div className={classes.moduleHeader}>
                <Typography className={classes.moduleTitle}>
                  {moduleIndex + 1}. {module.title}
                  {canManage && module.isActive === false && (
                    <span className={classes.inactiveBadge}>Inactivo</span>
                  )}
                </Typography>
                {canManage && (
                  <div className={classes.moduleActions}>
                    <Tooltip title="Nuevo tópico">
                      <IconButton size="small" onClick={() => openTopicModal(null, module.id)}>
                        <Add fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar módulo">
                      <IconButton size="small" onClick={() => openModuleModal(module)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar módulo">
                      <IconButton size="small" onClick={() => removeModule(module)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                )}
              </div>

              {(module.topics || []).map((topic, topicIndex) => {
                const active = Number(topic.id) === Number(selectedTopic?.id);
                return (
                  <button
                    key={topic.id}
                    type="button"
                    className={`${classes.topicButton} ${
                      active ? classes.topicButtonActive : ""
                    }`}
                    onClick={() => setSelectedTopicId(topic.id)}
                  >
                    <span className={classes.topicNumber}>
                      {moduleIndex + 1}.{topicIndex + 1}
                    </span>
                    <span className={classes.topicText}>
                      {topic.title}
                      {canManage && topic.isActive === false && (
                        <span className={classes.inactiveBadge}>Inactivo</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </aside>

        <main className={classes.workspace}>
          {!selectedTopic ? (
            <div className={classes.emptyState}>
              <PlayCircleOutline fontSize="large" />
              <Typography variant="h6">Selecciona un tópico</Typography>
              <Typography variant="body2">
                El contenido publicado aparecerá aquí.
              </Typography>
            </div>
          ) : (
            <>
              <div className={classes.courseHeader}>
                <div>
                  <Typography variant="overline" color="textSecondary">
                    {selectedModule?.title || "Tutorial"}
                  </Typography>
                  <Typography variant="h5">
                    {selectedTopic.title}
                    {canManage && selectedTopic.isActive === false && (
                      <span className={classes.inactiveBadge}>Inactivo</span>
                    )}
                  </Typography>
                </div>
                {canManage && (
                  <div className={classes.topicActions}>
                    <Tooltip title="Editar tópico">
                      <IconButton onClick={() => openTopicModal(selectedTopic)}>
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar tópico">
                      <IconButton onClick={() => removeTopic(selectedTopic)}>
                        <DeleteOutline />
                      </IconButton>
                    </Tooltip>
                  </div>
                )}
              </div>

              <div className={classes.videoShell}>
                {videoId ? (
                  <iframe
                    className={classes.iframe}
                    title={selectedTopic.title}
                    src={`https://www.youtube.com/embed/${videoId}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <div className={classes.videoPlaceholder}>
                    <PlayCircleOutline fontSize="large" />
                    <Typography variant="subtitle1">Video no configurado</Typography>
                  </div>
                )}
              </div>

              <div className={classes.contentBlock}>
                <MarkdownWrapper forceInline={false}>
                  {selectedTopic.content || "Sin contenido escrito."}
                </MarkdownWrapper>
              </div>
            </>
          )}
        </main>
      </Paper>

      <Dialog
        open={moduleModalOpen}
        onClose={() => setModuleModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingModule ? "Editar módulo" : "Nuevo módulo"}</DialogTitle>
        <DialogContent>
          <div className={classes.formGrid}>
            <TextField
              label="Título"
              value={moduleValues.title}
              onChange={event => {
                const { value } = event.target;
                setModuleValues(prev => ({ ...prev, title: value }));
              }}
              fullWidth
            />
            <TextField
              label="Orden"
              type="number"
              value={moduleValues.position}
              onChange={event => {
                const { value } = event.target;
                setModuleValues(prev => ({ ...prev, position: value }));
              }}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={moduleValues.isActive}
                  color="primary"
                  onChange={event => {
                    const { checked } = event.target;
                    setModuleValues(prev => ({
                      ...prev,
                      isActive: checked,
                    }));
                  }}
                />
              }
              label="Activo"
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModuleModalOpen(false)}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={saveModule}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={topicModalOpen}
        onClose={() => setTopicModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editingTopic ? "Editar tópico" : "Nuevo tópico"}</DialogTitle>
        <DialogContent>
          <div className={classes.formGrid}>
            <TextField
              select
              label="Módulo"
              value={topicValues.moduleId}
              onChange={event => {
                const { value } = event.target;
                setTopicValues(prev => ({ ...prev, moduleId: value }));
              }}
              fullWidth
            >
              {orderedModules.map(module => (
                <MenuItem key={module.id} value={module.id}>
                  {module.title}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Título"
              value={topicValues.title}
              onChange={event => {
                const { value } = event.target;
                setTopicValues(prev => ({ ...prev, title: value }));
              }}
              fullWidth
            />
            <TextField
              label="URL de YouTube"
              value={topicValues.youtubeUrl}
              onChange={event => {
                const { value } = event.target;
                setTopicValues(prev => ({ ...prev, youtubeUrl: value }));
              }}
              fullWidth
            />
            <TextField
              label="Orden"
              type="number"
              value={topicValues.position}
              onChange={event => {
                const { value } = event.target;
                setTopicValues(prev => ({ ...prev, position: value }));
              }}
              fullWidth
            />
            <TextField
              label="Contenido"
              value={topicValues.content}
              onChange={event => {
                const { value } = event.target;
                setTopicValues(prev => ({ ...prev, content: value }));
              }}
              multiline
              minRows={8}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={topicValues.isActive}
                  color="primary"
                  onChange={event => {
                    const { checked } = event.target;
                    setTopicValues(prev => ({
                      ...prev,
                      isActive: checked,
                    }));
                  }}
                />
              }
              label="Activo"
            />
            <div className={classes.preview}>
              <Typography variant="subtitle2" gutterBottom>
                Vista previa
              </Typography>
              <MarkdownWrapper forceInline={false}>
                {topicValues.content || "Sin contenido escrito."}
              </MarkdownWrapper>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTopicModalOpen(false)}>Cancelar</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={saveTopic}
            disabled={!topicValues.moduleId}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Tutorials;
